import { useState, useEffect, useRef } from "react";
import { Plus, Ticket, CalendarClock, Camera, PhoneOff } from "lucide-react";

// Floating quick-add button that sits above the pill nav and opens three
// consistent "add" actions no matter which tab you're on. It slides away with
// the bottom nav when you scroll down (and slides back when the nav returns),
// so it never floats orphaned or crowds the back-to-top arrow.
export const QuickAddFab = ({ onAddTour, onAddShift, onImport, onAddTimeOff }) => {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);

  // Mirror BottomNav: hide while scrolling down, reveal on scroll up / near the top.
  useEffect(() => {
    lastY.current = window.scrollY;
    const onScroll = (e) => {
      const t = e && e.target;
      let y;
      if (!t || t === document || t === window || t === document.documentElement || t === document.body) y = window.scrollY;
      else if (typeof t.scrollTop === "number") y = t.scrollTop;
      else y = window.scrollY;
      if (y < 12) setHidden(false);
      else if (y > lastY.current + 8) { setHidden(true); setOpen(false); }
      else if (y < lastY.current - 8) setHidden(false);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    return () => window.removeEventListener("scroll", onScroll, { capture: true });
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const act = (fn) => () => { setOpen(false); fn?.(); };

  const actions = [
    { key: "tour", label: "Add tour", icon: Ticket, onClick: act(onAddTour), testid: "quick-add-tour" },
    { key: "shift", label: "Add shift", icon: CalendarClock, onClick: act(onAddShift), testid: "quick-add-shift" },
    { key: "timeoff", label: "Add time off", icon: PhoneOff, onClick: act(onAddTimeOff), testid: "quick-add-timeoff" },
    { key: "import", label: "Import screenshot", icon: Camera, onClick: act(onImport), testid: "quick-add-import" },
  ];

  return (
    <>
      {open && (
        <button
          aria-label="Close quick add"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[44] bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-150"
          data-testid="quick-add-backdrop"
        />
      )}
      <div
        className="pointer-events-none fixed right-4 z-[46] flex flex-col items-end gap-3"
        style={{
          bottom: "calc(5.25rem + env(safe-area-inset-bottom))",
          transform: hidden ? "translateY(160%)" : "translateY(0)",
          opacity: hidden ? 0 : 1,
          transition: "transform 300ms ease, opacity 300ms ease",
        }}
        aria-hidden={hidden}
      >
        {open && (
          <div className="pointer-events-auto flex flex-col items-end gap-2.5">
            {actions.map((a, i) => {
              const Icon = a.icon;
              return (
                <button
                  key={a.key}
                  onClick={a.onClick}
                  data-testid={a.testid}
                  style={{ animationDelay: `${i * 45}ms` }}
                  className="flex items-center gap-2.5 rounded-full border border-zinc-700/70 bg-zinc-900/95 py-2 pl-4 pr-2 text-sm font-600 text-zinc-100 shadow-xl shadow-black/40 backdrop-blur transition-transform duration-150 animate-in fade-in slide-in-from-bottom-2 active:scale-95"
                >
                  <span className="whitespace-nowrap">{a.label}</span>
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#9B2226]/20 text-[#E0575B]">
                    <Icon className="h-4 w-4" />
                  </span>
                </button>
              );
            })}
          </div>
        )}
        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="quick-add-fab"
          aria-label={open ? "Close quick add" : "Quick add"}
          aria-expanded={open}
          tabIndex={hidden ? -1 : 0}
          className={`flex h-14 w-14 items-center justify-center rounded-full bg-[#9B2226] text-white shadow-xl shadow-[#9B2226]/30 transition-transform duration-200 hover:bg-[#7A1A1E] active:scale-90 ${hidden ? "pointer-events-none" : "pointer-events-auto"}`}
        >
          <Plus className={`h-6 w-6 transition-transform duration-300 ${open ? "rotate-[135deg]" : "rotate-0"}`} />
        </button>
      </div>
    </>
  );
};
